import { useState } from 'react'
import { Button, Dropdown, Label, ToggleButton, ToggleButtonGroup } from '@heroui/react'
import { LANGUAGES } from '../lib/i18n/languages'
import { nativeName } from '../lib/i18n/customer'
import { Icon } from '../ui/icons'
import { useStaffText } from '../ui/StaffText'
import { MoreMenu } from './MoreMenu'
import { Notice, StudioModal, StudioSelect } from './heroParts'
import { MAX_ROTATE_SEC, MIN_ROTATE_SEC, ROTATE_CHOICES, withRotation } from './designLanguages'
import type { LayoutDocument } from './types'

const OTHER = '*other'

/** A display's "Languages on screen": the first tab only, or each in turn. */
export function ScreenLanguagesPanel({
  document: doc,
  languages,
  onReplace,
}: {
  document: LayoutDocument
  /** The design's languages, in tab order. */
  languages: readonly string[]
  onReplace: (doc: LayoutDocument) => void
}) {
  const { say } = useStaffText()
  const now = languages.length > 1 ? (doc.rotateSec ?? 0) : 0
  const choices = now === 0 || ROTATE_CHOICES.includes(now) ? ROTATE_CHOICES : [...ROTATE_CHOICES, now].sort((a, b) => a - b)
  return (
    <section className="studio-panel">
      <div className="studio-panel__head">
        <h2>{say('designs.lang.onScreen')}</h2>
      </div>
      <StudioSelect
        aria-label={say('designs.lang.onScreen')}
        value={String(now)}
        isDisabled={languages.length < 2}
        onChange={(value) => onReplace(withRotation(doc, Number(value)))}
        options={[
          { value: '0', label: say('designs.lang.one') },
          ...choices
            .filter((n) => n >= MIN_ROTATE_SEC && n <= MAX_ROTATE_SEC)
            .map((n) => ({ value: String(n), label: say('designs.lang.every', { n }) })),
        ]}
      />
    </section>
  )
}

/**
 * A design's languages as tabs under the canvas: the one chosen is the one the
 * inspector edits. "+" adds another of the organisation's customer languages, or
 * (onAddOther set) a new one to the organisation too. The chosen tab moves or goes.
 */
export function LanguageTabs({
  languages,
  offered,
  value,
  onPick,
  onAdd,
  onRemove,
  onMove,
  onAddOther,
}: {
  /** The design's, in tab order; the first is what a screen starts in. */
  languages: readonly string[]
  /** The organisation's customer languages. */
  offered: readonly string[]
  value: string
  onPick: (code: string) => void
  onAdd: (code: string) => void
  onRemove: (code: string) => void
  onMove: (code: string, by: -1 | 1) => void
  /** Adds a language to the organisation and to the design; the refusal to show, or null. Absent hides Other. */
  onAddOther?: (code: string) => Promise<string | null>
}) {
  const { say } = useStaffText()
  const [asking, setAsking] = useState(false)
  const addable = offered.filter((c) => !languages.includes(c))
  const at = languages.indexOf(value)
  return (
    <span className="studio-langtabs">
      <ToggleButtonGroup
        size="sm"
        selectionMode="single"
        disallowEmptySelection
        aria-label={say('designs.lang.label')}
        selectedKeys={new Set([value])}
        onSelectionChange={(keys) => {
          const next = [...keys][0]
          if (next !== undefined) onPick(String(next))
        }}
      >
        {languages.map((l) => (
          <ToggleButton key={l} id={l} aria-label={nativeName(l)}>
            {l.toUpperCase()}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
      {(addable.length > 0 || onAddOther) && (
        <Dropdown>
          <Button isIconOnly variant="ghost" size="sm" aria-label={say('designs.lang.add')}>
            <Icon name="plus" size={14} />
          </Button>
          <Dropdown.Popover placement="top end">
            <Dropdown.Menu onAction={(key) => (key === OTHER ? setAsking(true) : onAdd(String(key)))}>
              {[
                ...addable.map((c) => (
                  <Dropdown.Item key={c} id={c} textValue={nativeName(c)}>
                    <Label>{nativeName(c)}</Label>
                  </Dropdown.Item>
                )),
                ...(onAddOther
                  ? [
                      <Dropdown.Item key={OTHER} id={OTHER} textValue={say('designs.lang.other')}>
                        <Label>{say('designs.lang.other')}</Label>
                      </Dropdown.Item>,
                    ]
                  : []),
              ]}
            </Dropdown.Menu>
          </Dropdown.Popover>
        </Dropdown>
      )}
      {languages.length > 1 && at >= 0 && (
        <MoreMenu
          up
          items={[
            { label: say('designs.lang.moveLeft'), icon: 'left', onClick: () => onMove(value, -1), disabled: at === 0 },
            { label: say('designs.lang.moveRight'), icon: 'right', onClick: () => onMove(value, 1), disabled: at === languages.length - 1 },
            { label: say('designs.lang.remove'), icon: 'trash', onClick: () => onRemove(value), danger: true },
          ]}
        />
      )}
      {asking && onAddOther && <OtherLanguage offered={offered} onAdd={onAddOther} onClose={() => setAsking(false)} />}
    </span>
  )
}

function OtherLanguage({
  offered,
  onAdd,
  onClose,
}: {
  offered: readonly string[]
  onAdd: (code: string) => Promise<string | null>
  onClose: () => void
}) {
  const { say } = useStaffText()
  const choices = LANGUAGES.filter((l) => !offered.includes(l.code))
  const [code, setCode] = useState(choices[0]?.code ?? '')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const add = async () => {
    if (!code || busy) return
    setBusy(true)
    const refused = await onAdd(code)
    setBusy(false)
    if (refused) setError(refused)
    else onClose()
  }
  return (
    <StudioModal
      title={say('designs.lang.otherTitle')}
      size="sm"
      onClose={onClose}
      onSubmit={() => void add()}
      footer={
        <>
          <Button variant="tertiary" size="sm" onPress={onClose}>
            {say('common.cancel')}
          </Button>
          <Button type="submit" variant="primary" size="sm" isDisabled={!code || busy}>
            {say('common.add')}
          </Button>
        </>
      }
    >
      <StudioSelect
        label={say('eq.sheet.language')}
        value={code}
        onChange={setCode}
        options={choices.map((l) => ({ value: l.code, label: `${l.native} · ${l.name}` }))}
      />
      {error && <Notice role="alert">{error}</Notice>}
    </StudioModal>
  )
}
